import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpToLine,
  Check,
  ExternalLink,
  Eye,
  Pencil,
  RotateCcw,
  Send,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import {
  type ChannelDetail,
  type Delivery,
  type DeliveryAction,
  type DeliveryStatus,
  type DeliveryView,
  byPublisher,
  deliveryTone,
  sourceLabel,
  useBulkDeliveries,
  useDeliveries,
  useDeliveryAction,
  useEditDelivery,
  useMoveDelivery,
  useRegenerateDelivery,
} from "@/lib/aggregator";
import { formatMoment, formatNumber, formatRelative } from "@/lib/format";
import { pushToast } from "@/components/Toaster";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ConfirmDialog,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SimpleSelect } from "@/components/ui/select";
import { ICON_BUTTON, TEXTAREA, errorText, reasonText } from "./helpers";
import { MediaStrip, RichTextView } from "./parts";

const JOURNAL_FILTERS: readonly (DeliveryStatus | "all")[] = [
  "all",
  "published",
  "skipped",
  "failed",
  "expired",
  "rejected",
  "removed",
];
const PAGE = 30;
/** As many as one list shows (and as one "all of these" may name). */
const MOST = 500;

/**
 * The channel's posts, one of three ways: the queue (what goes out next, and
 * about when), the review (what waits for a person), the journal (everything
 * read for the channel and what became of it — with the reason when it did
 * not come out).
 */
export function PostsTab({
  channel,
  view,
  active,
  canEdit,
}: {
  channel: ChannelDetail;
  view: DeliveryView;
  /** The tab is on screen: only then is the list kept fresh. */
  active: boolean;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const [only, setOnly] = useState<DeliveryStatus | "all">("all");
  const [source, setSource] = useState("all");
  const [limit, setLimit] = useState(PAGE);
  const posts = useDeliveries(
    active ? channel.id : null,
    view,
    {
      only: view === "journal" && only !== "all" ? only : undefined,
      source_id: source !== "all" ? source : undefined,
      limit,
    },
    active,
  );
  const act = useDeliveryAction();
  const move = useMoveDelivery();
  const bulk = useBulkDeliveries();
  const [editing, setEditing] = useState<Delivery | null>(null);
  const [asking, setAsking] = useState<Delivery | null>(null);
  const [confirming, setConfirming] = useState<{ delivery: Delivery; action: DeliveryAction } | null>(
    null,
  );

  const run = async (delivery: Delivery, action: DeliveryAction) => {
    try {
      await act.mutateAsync({ delivery, action });
      pushToast({ kind: "success", text: t(`aggregator.post.done.${action}`) });
    } catch (err) {
      pushToast({ kind: "error", text: errorText(err, t("aggregator.action_error")) });
    }
  };

  const items = posts.data?.items ?? [];
  // "All" is what is on screen: when there is more than that, the buttons say so.
  const partly = (posts.data?.total ?? 0) > items.length;
  // Up and down among a post's own kind: while a fill runs, fresh posts and
  // the fill's go out by turns, each kind in its own order.
  const sameKind = (of: { fill_id: string | null }) =>
    items.filter((i) => i.status === "queued" && (i.fill_id === null) === (of.fill_id === null));
  const forAll = (action: "approve" | "reject" | "retry") =>
    t(`aggregator.post.${action}_${partly ? "shown" : "all"}`, { count: items.length });
  const busy = (delivery: Delivery, action: DeliveryAction) =>
    act.isPending && act.variables?.delivery.id === delivery.id && act.variables.action === action;
  // The rows on screen are still those of the filter before: "approve all"
  // pressed now would approve posts the person is no longer looking at.
  const stale = posts.isPlaceholderData;

  const everything = async (action: "approve" | "reject" | "retry") => {
    try {
      const out = await bulk.mutateAsync({ id: channel.id, ids: items.map((d) => d.id), action });
      pushToast({ kind: "success", text: t(`aggregator.post.bulk_done.${action}`, { count: out.done }) });
      if (action === "retry" && out.skipped > 0) {
        pushToast({
          kind: "warning",
          text: t("aggregator.post.bulk_retry_skipped", { count: out.skipped }),
        });
      }
    } catch (err) {
      pushToast({ kind: "error", text: errorText(err, t("aggregator.action_error")) });
    }
  };

  return (
    <div className="space-y-4">
      {view === "queue" && channel.status === "active" && channel.problem && (
        <Alert tone="warning">{t("aggregator.queue.held")}</Alert>
      )}
      {view === "queue" && channel.status === "paused" && (
        <Alert tone="warning">{t("aggregator.queue.paused")}</Alert>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {view === "journal" && (
          <SimpleSelect
            className="h-9 w-auto min-w-[11rem]"
            value={only}
            onValueChange={(value) => {
              setOnly(value as DeliveryStatus | "all");
              setLimit(PAGE);
            }}
            options={JOURNAL_FILTERS.map((value) => ({
              value,
              label: t(value === "all" ? "aggregator.journal.all" : `aggregator.status.${value}`),
            }))}
          />
        )}
        {channel.source_list.length > 1 && (
          <SimpleSelect
            className="h-9 w-auto min-w-[11rem]"
            value={source}
            onValueChange={(value) => {
              setSource(value);
              setLimit(PAGE);
            }}
            options={[
              { value: "all", label: t("aggregator.rules.every_source") },
              ...channel.source_list.map((s) => ({
                value: s.id,
                label: sourceLabel(s, t("aggregator.sources.closed_name")),
              })),
            ]}
          />
        )}
        <span className="ml-auto text-xs text-fg-muted">
          {posts.data ? t("aggregator.posts.total", { count: posts.data.total }) : ""}
        </span>
        {canEdit && view === "review" && items.length > 1 && (
          <>
            <Button
              size="sm"
              variant="outline"
              loading={bulk.isPending && bulk.variables?.action === "approve"}
              disabled={stale}
              onClick={() => everything("approve")}
            >
              {forAll("approve")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              loading={bulk.isPending && bulk.variables?.action === "reject"}
              disabled={stale}
              onClick={() => everything("reject")}
            >
              {forAll("reject")}
            </Button>
          </>
        )}
        {canEdit && view === "journal" && only === "failed" && items.length > 1 && (
          <Button
            size="sm"
            variant="outline"
            loading={bulk.isPending && bulk.variables?.action === "retry"}
            disabled={stale}
            onClick={() => everything("retry")}
          >
            {forAll("retry")}
          </Button>
        )}
      </div>

      {posts.isLoading && <p className="text-sm text-fg-muted">{t("aggregator.loading")}</p>}
      {posts.isError && <Alert tone="danger">{t("aggregator.load_error")}</Alert>}
      {posts.data && items.length === 0 && (
        <EmptyState
          icon={view === "review" ? Eye : Send}
          title={t(`aggregator.${view}.empty_title`)}
          description={t(`aggregator.${view}.empty_hint`)}
        />
      )}

      <ul className="space-y-3">
        {items.map((delivery, n) => (
          <li
            key={delivery.id}
            className="space-y-3 rounded-xl border border-border bg-bg-primary px-4 py-3"
          >
            <div className="flex flex-wrap items-center gap-2 text-xs text-fg-muted">
              <Badge tone={deliveryTone(delivery.status)}>
                {t(`aggregator.status.${delivery.status}`)}
              </Badge>
              <a
                href={delivery.source_url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex min-w-0 items-center gap-1 hover:underline"
              >
                <span className="truncate">
                  {delivery.source_title ?? `@${delivery.source_username}`}
                </span>
                <ExternalLink size={12} strokeWidth={2} aria-hidden="true" className="shrink-0" />
              </a>
              {delivery.posted_at && <span>{formatRelative(delivery.posted_at)}</span>}
              {delivery.ai_provider && (
                <Badge tone="accent" icon={<Sparkles size={11} aria-hidden="true" />}>
                  {delivery.ai_provider}
                </Badge>
              )}
              {delivery.edited && <Badge>{t("aggregator.post.edited")}</Badge>}
              {delivery.fill_id && <Badge>{t("aggregator.post.from_fill")}</Badge>}
              <span className="ml-auto text-right">
                {delivery.status === "queued" &&
                  delivery.expected_at &&
                  // The freshest go first: a post far down the queue may be
                  // let go before its turn, and the page says so.
                  (delivery.expires_at &&
                  Date.parse(delivery.expected_at) > Date.parse(delivery.expires_at) ? (
                    <span className="text-warning">
                      {t("aggregator.queue.wont_make_it", {
                        when: formatMoment(delivery.expires_at, channel.timezone),
                      })}
                    </span>
                  ) : (
                    <>
                      {t("aggregator.queue.expected", {
                        when: formatMoment(delivery.expected_at, channel.timezone),
                      })}
                    </>
                  ))}
                {delivery.status === "published" && delivery.published_at && (
                  <>
                    {t("aggregator.post.published_at", { when: formatRelative(delivery.published_at) })}
                    {delivery.views !== null &&
                      ` · ${t("aggregator.post.views", { n: formatNumber(delivery.views) })}`}
                  </>
                )}
              </span>
            </div>

            {delivery.reason && (
              <p
                className={
                  delivery.status === "failed" ? "text-xs text-danger" : "text-xs text-fg-secondary"
                }
              >
                {reasonText(t, delivery.reason, delivery.detail)}
              </p>
            )}

            <MediaStrip media={delivery.media} missing={delivery.media_missing} t={t} />

            {delivery.text && delivery.text.markup !== delivery.original.markup ? (
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <div className="mb-1 text-xs font-medium text-fg-muted">
                    {t("aggregator.post.original")}
                  </div>
                  <RichTextView text={delivery.original} clamp className="opacity-80" />
                </div>
                <div>
                  <div className="mb-1 text-xs font-medium text-fg-muted">
                    {t("aggregator.post.result")}
                  </div>
                  <RichTextView text={delivery.text} clamp />
                </div>
              </div>
            ) : (
              <RichTextView text={delivery.text ?? delivery.original} clamp />
            )}

            {canEdit && (
              <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
                {delivery.status === "review" && (
                  <Button
                    size="sm"
                    loading={busy(delivery, "approve")}
                    onClick={() => run(delivery, "approve")}
                    iconLeft={<Check size={15} strokeWidth={2} aria-hidden="true" />}
                  >
                    {t("aggregator.post.approve")}
                  </Button>
                )}
                {(delivery.status === "review" || delivery.status === "queued") && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      loading={busy(delivery, "publish-now")}
                      onClick={() => setConfirming({ delivery, action: "publish-now" })}
                      iconLeft={<Send size={14} strokeWidth={2} aria-hidden="true" />}
                    >
                      {t("aggregator.post.publish_now")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditing(delivery)}
                      iconLeft={<Pencil size={14} strokeWidth={2} aria-hidden="true" />}
                    >
                      {t("aggregator.post.edit")}
                    </Button>
                    {channel.settings.ai.mode !== "off" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAsking(delivery)}
                        iconLeft={<Sparkles size={14} strokeWidth={2} aria-hidden="true" />}
                      >
                        {t("aggregator.post.regenerate")}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-danger hover:text-danger"
                      loading={busy(delivery, "reject")}
                      onClick={() => run(delivery, "reject")}
                      iconLeft={<X size={15} strokeWidth={2} aria-hidden="true" />}
                    >
                      {t("aggregator.post.reject")}
                    </Button>
                  </>
                )}
                {delivery.status === "queued" && (
                  <span className="ml-auto flex items-center">
                    {(
                      [
                        ["top", ArrowUpToLine],
                        ["up", ArrowUp],
                        ["down", ArrowDown],
                      ] as const
                    ).map(([to, Icon]) => (
                      <button
                        key={to}
                        type="button"
                        className={ICON_BUTTON}
                        disabled={
                          move.isPending ||
                          (to === "top" && n === 0) ||
                          (to === "up" && sameKind(delivery)[0]?.id === delivery.id) ||
                          (to === "down" && sameKind(delivery).at(-1)?.id === delivery.id)
                        }
                        aria-label={t(`aggregator.queue.move_${to}`)}
                        title={t(`aggregator.queue.move_${to}`)}
                        onClick={() =>
                          move.mutate(
                            { delivery, to },
                            {
                              onError: (err) =>
                                pushToast({
                                  kind: "error",
                                  text: errorText(err, t("aggregator.action_error")),
                                }),
                            },
                          )
                        }
                      >
                        <Icon size={16} strokeWidth={2} aria-hidden="true" />
                      </button>
                    ))}
                  </span>
                )}
                {delivery.status === "published" && (
                  <>
                    {delivery.url && (
                      <a
                        href={delivery.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border px-3 text-sm font-medium hover:bg-bg-elevated/50"
                      >
                        <ExternalLink size={14} strokeWidth={2} aria-hidden="true" />
                        {t("aggregator.post.open")}
                      </a>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditing(delivery)}
                      iconLeft={<Pencil size={14} strokeWidth={2} aria-hidden="true" />}
                    >
                      {t("aggregator.post.fix")}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-danger hover:text-danger"
                      onClick={() => setConfirming({ delivery, action: "unpublish" })}
                      iconLeft={<Trash2 size={14} strokeWidth={2} aria-hidden="true" />}
                    >
                      {t("aggregator.post.unpublish")}
                    </Button>
                  </>
                )}
                {delivery.left_in_channel && (
                  // A part of the post is in the channel and would not come
                  // out when it failed. Sent again as it is, that part would
                  // be there twice: it is taken down first.
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-danger hover:text-danger"
                    loading={busy(delivery, "unpublish")}
                    onClick={() => setConfirming({ delivery, action: "unpublish" })}
                    iconLeft={<Trash2 size={14} strokeWidth={2} aria-hidden="true" />}
                  >
                    {t("aggregator.post.take_down_left")}
                  </Button>
                )}
                {["failed", "skipped", "expired", "rejected"].includes(delivery.status) &&
                  !delivery.left_in_channel && (
                  <Button
                    size="sm"
                    variant="outline"
                    loading={busy(delivery, "retry")}
                    onClick={() => run(delivery, "retry")}
                    iconLeft={<RotateCcw size={14} strokeWidth={2} aria-hidden="true" />}
                  >
                    {t(
                      delivery.status === "failed"
                        ? "aggregator.post.retry"
                        : "aggregator.post.take_anyway",
                    )}
                  </Button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {posts.data && posts.data.total > items.length && limit < MOST && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => setLimit((n) => Math.min(MOST, n + PAGE))}>
            {t("aggregator.posts.more")}
          </Button>
        </div>
      )}
      {posts.data && posts.data.total > items.length && limit >= MOST && (
        <p className="text-center text-xs text-fg-muted">{t("aggregator.posts.capped")}</p>
      )}

      <EditDialog delivery={editing} onClose={() => setEditing(null)} />
      <RegenerateDialog delivery={asking} onClose={() => setAsking(null)} />
      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(o) => !o && setConfirming(null)}
        tone={confirming?.action === "unpublish" ? "danger" : "default"}
        title={t(`aggregator.post.confirm.${confirming?.action ?? "publish-now"}`)}
        description={t(
          byPublisher(`aggregator.post.confirm_hint.${confirming?.action ?? "publish-now"}`),
        )}
        confirmText={t(
          confirming?.action === "unpublish"
            ? "aggregator.post.unpublish"
            : "aggregator.post.publish_now",
        )}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          if (confirming) await run(confirming.delivery, confirming.action);
        }}
      />
    </div>
  );
}

/** The text of a post — before it goes out, or already in the channel. */
export function EditDialog({
  delivery,
  onClose,
}: {
  delivery: Delivery | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const edit = useEditDelivery();
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (delivery) {
      setText((delivery.text ?? delivery.original).markup);
      setError(null);
    }
  }, [delivery]);

  const published = delivery?.status === "published";
  const submit = async () => {
    if (!delivery) return;
    setError(null);
    try {
      await edit.mutateAsync({ delivery, text });
      pushToast({
        kind: "success",
        text: t(published ? "aggregator.post.fixed" : "aggregator.post.saved"),
      });
      onClose();
    } catch (err) {
      setError(errorText(err, t("aggregator.action_error")));
    }
  };

  return (
    <Dialog open={delivery !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            {t(published ? "aggregator.post.fix_title" : "aggregator.post.edit_title")}
          </DialogTitle>
          <DialogDescription>
            {t(published ? byPublisher("aggregator.post.fix_hint") : ["aggregator.post.edit_hint"])}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={12}
            maxLength={12000}
            className={TEXTAREA}
            aria-label={t("aggregator.post.text")}
          />
          <p className="text-xs text-fg-muted">{t("aggregator.markup_hint")}</p>
          {delivery && (
            <details className="text-xs text-fg-muted">
              <summary className="cursor-pointer">{t("aggregator.post.original")}</summary>
              <RichTextView text={delivery.original} className="mt-2 opacity-80" />
            </details>
          )}
          {error && <Alert tone="danger">{error}</Alert>}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} loading={edit.isPending} disabled={edit.isPending}>
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Ask the model for another wording, with a hint if the editor has one. */
function RegenerateDialog({ delivery, onClose }: { delivery: Delivery | null; onClose: () => void }) {
  const { t } = useTranslation();
  const regenerate = useRegenerateDelivery();
  const [hint, setHint] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (delivery) {
      setHint("");
      setError(null);
    }
  }, [delivery]);

  const submit = async () => {
    if (!delivery) return;
    setError(null);
    try {
      await regenerate.mutateAsync({ delivery, hint });
      pushToast({ kind: "success", text: t("aggregator.post.regenerated") });
      onClose();
    } catch (err) {
      setError(errorText(err, t("aggregator.action_error")));
    }
  };

  return (
    <Dialog open={delivery !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("aggregator.post.regenerate_title")}</DialogTitle>
          <DialogDescription>{t("aggregator.post.regenerate_hint")}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="agg_hint">{t("aggregator.post.hint")}</Label>
            <Input
              id="agg_hint"
              value={hint}
              maxLength={500}
              onChange={(e) => setHint(e.target.value)}
              placeholder={t("aggregator.post.hint_placeholder")}
            />
          </div>
          {error && <Alert tone="danger">{error}</Alert>}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} loading={regenerate.isPending} disabled={regenerate.isPending}>
            {t("aggregator.post.regenerate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
